import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { Link } from 'react-router-dom'
import { ArrowRight, Download, FileText, Filter, Search, Settings2 } from 'lucide-react'
import type { ReportDomain, ReportMetadata } from '../../types'
import { DOMAIN_LABELS } from '../../types'
import { DOMAIN_COLOUR_MAP, DOMAIN_ICON_MAP, DOMAIN_ORDER, ICON_MAP } from './reports-hub.helpers'

interface ReportCardProps {
  report: ReportMetadata
  t: TFunction<'admin'>
}

export function ReportCard({ report, t }: ReportCardProps): ReactElement {
  const Icon = ICON_MAP[report.icon] ?? FileText
  const colours = DOMAIN_COLOUR_MAP[report.domain]
  return (
    <Link
      to={report.path}
      className="group flex flex-col rounded-xl border border-stone-200 bg-white p-6 transition-all hover:border-[#8a9bba] hover:shadow-md dark:border-stone-800 dark:bg-stone-900 dark:hover:border-[#5c6e94]"
    >
      <div className="flex items-start justify-between">
        <div className={`rounded-lg p-3 ${colours.bg}`}>
          <Icon className={`h-6 w-6 ${colours.text}`} />
        </div>
        <div className="flex gap-2">
          {report.queryable && (
            <span
              className="rounded-md bg-stone-100 p-1.5 dark:bg-stone-800"
              title="Supports filters"
            >
              <Settings2 className="h-4 w-4 text-stone-500 dark:text-stone-400" />
            </span>
          )}
          {report.exportable && (
            <span className="rounded-md bg-stone-100 p-1.5 dark:bg-stone-800" title="Exportable">
              <Download className="h-4 w-4 text-stone-500 dark:text-stone-400" />
            </span>
          )}
        </div>
      </div>
      <h3 className="mt-4 font-semibold text-stone-900 dark:text-stone-100">{report.name}</h3>
      <p className="mt-2 flex-1 text-sm text-stone-600 dark:text-stone-400">{report.description}</p>
      <div className="mt-4 flex items-center gap-2 text-sm font-medium text-[#5c6e94] transition-colors group-hover:text-[#4e5e82] dark:text-[#8a9bba]">
        {t('reportsHub.viewReport')}{' '}
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </div>
    </Link>
  )
}

interface DomainSectionProps {
  domain: ReportDomain
  reports: ReportMetadata[]
  t: TFunction<'admin'>
}

export function DomainSection({ domain, reports, t }: DomainSectionProps): ReactElement {
  const DomainIcon = DOMAIN_ICON_MAP[domain]
  const colours = DOMAIN_COLOUR_MAP[domain]
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className={`rounded-lg p-2 ${colours.bg}`}>
          <DomainIcon className={`h-5 w-5 ${colours.text}`} />
        </div>
        <h2 className="text-lg font-semibold text-stone-900 dark:text-stone-100">
          {DOMAIN_LABELS[domain]}
        </h2>
        <span className="rounded-full bg-stone-100 px-2 py-0.5 text-xs font-medium text-stone-600 dark:bg-stone-800 dark:text-stone-400">
          {t('reportsHub.reportCount', { count: reports.length })}
        </span>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {reports.map((report) => (
          <ReportCard key={report.id} report={report} t={t} />
        ))}
      </div>
    </div>
  )
}

interface FiltersBarProps {
  searchQuery: string
  onSearchChange: (value: string) => void
  selectedDomain: ReportDomain | 'all'
  onDomainChange: (value: ReportDomain | 'all') => void
  t: TFunction<'admin'>
}

export function FiltersBar({
  searchQuery,
  onSearchChange,
  selectedDomain,
  onDomainChange,
  t,
}: FiltersBarProps): ReactElement {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="relative flex-1 sm:max-w-md">
        <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-stone-400" />
        <input
          type="text"
          placeholder={t('reportsHub.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => {
            onSearchChange(e.target.value)
          }}
          className="w-full rounded-lg border border-stone-200 bg-white py-2 pr-4 pl-10 text-sm text-stone-900 placeholder:text-stone-400 focus:border-[#4e5e82] focus:ring-1 focus:ring-[#4e5e82] focus:outline-none dark:border-stone-700 dark:bg-stone-800 dark:text-stone-100 dark:focus:border-[#4e5e82]"
        />
      </div>
      <div className="flex items-center gap-2">
        <Filter className="h-4 w-4 text-stone-400" />
        <select
          value={selectedDomain}
          onChange={(e) => {
            onDomainChange(e.target.value as ReportDomain | 'all')
          }}
          className="rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm text-stone-900 focus:border-[#4e5e82] focus:ring-1 focus:ring-[#4e5e82] focus:outline-none dark:border-stone-700 dark:bg-stone-800 dark:text-stone-100"
        >
          <option value="all">{t('reportsHub.allDomains')}</option>
          {DOMAIN_ORDER.map((domain) => (
            <option key={domain} value={domain}>
              {DOMAIN_LABELS[domain]}
            </option>
          ))}
        </select>
      </div>
    </div>
  )
}

interface EmptyStateProps {
  hasFilters: boolean
}

export function EmptyState({ hasFilters }: EmptyStateProps): ReactElement {
  return (
    <div className="flex h-48 flex-col items-center justify-center rounded-xl border border-dashed border-stone-300 bg-stone-50 dark:border-stone-700 dark:bg-stone-900">
      <FileText className="h-12 w-12 text-stone-300 dark:text-stone-600" />
      <p className="mt-4 text-stone-500 dark:text-stone-400">
        {hasFilters ? 'No reports match your filters' : 'No reports available'}
      </p>
    </div>
  )
}

export function LoadingSpinner(): ReactElement {
  return (
    <div className="flex h-64 items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#4e5e82] border-t-transparent" />
    </div>
  )
}

interface ErrorViewProps {
  message: string
  retryLabel: string
  onRetry: () => void
}

export function ErrorView({ message, retryLabel, onRetry }: ErrorViewProps): ReactElement {
  return (
    <div className="flex h-64 flex-col items-center justify-center gap-4">
      <p className="text-red-600 dark:text-red-400">{message}</p>
      <button
        onClick={onRetry}
        className="rounded-lg bg-[#5c6e94] px-4 py-2 text-sm font-medium text-white hover:bg-[#4e5e82]"
      >
        {retryLabel}
      </button>
    </div>
  )
}

interface PageHeaderProps {
  t: TFunction<'admin'>
}

export function PageHeader({ t }: PageHeaderProps): ReactElement {
  return (
    <div>
      <h1 className="text-2xl font-semibold text-stone-900 dark:text-stone-100">
        {t('reportsHub.title')}
      </h1>
      <p className="mt-1 text-stone-600 dark:text-stone-400">{t('reportsHub.description')}</p>
    </div>
  )
}
